/**
 * Playground configuration helpers.
 *
 * Playground mode enables H5 apps to run in browser (via Vite) while communicating
 * with the real Firebase backend.
 *
 * Authentication flow (dev/staging):
 * 1. Vite plugin reads game.config.{env}.json and injects Firebase config via window.__RUNDOT_GAME_PLAYGROUND__
 * 2. Developer signs in via Google Sign-In in the playground toolbar
 * 3. All API calls use Firebase Auth session automatically
 *
 * Authentication flow (local emulator):
 * 1. Vite plugin reads game.config.{env}.json for gameId
 * 2. User signs in via toolbar (email/password against Auth emulator)
 */

export type PlaygroundTarget = 'local' | 'dev' | 'staging' | 'playground'

/**
 * Firebase configuration for a specific environment.
 */
export interface PlaygroundFirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  appId?: string
  storageBucket?: string
  messagingSenderId?: string
  measurementId?: string
}

/**
 * Configuration injected by the Vite dev server for playground mode.
 */
export interface PlaygroundConfig {
  /** Whether playground mode is enabled */
  enabled: boolean
  /** Target environment: local (emulator), dev, or staging */
  target: PlaygroundTarget
  /** Game/App ID from game.config.{env}.json */
  gameId: string

  /** Firebase configuration for the target environment */
  firebaseConfig?: PlaygroundFirebaseConfig
  /** Cloud Functions region */
  functionsRegion?: string

  /**
   * Base URL for backend RPC calls.
   * For local: http://localhost:5001/{projectId}/{region}
   * For dev/staging: Cloud Run proxy URL from CLI config
   */
  backendUrl?: string

  /** Base URL for Cloud Run API calls (for UGC and other Cloud Run services) */
  cloudRunUrl?: string

  /**
   * Hosted multiplayer room-server base URL. Injected per target by the Vite
   * plugin (only the `playground` target is populated). When set, PlaygroundHost
   * uses a real authenticated WsMultiplayerApi against this hosted server instead
   * of the local dev sidecar / offline mock.
   */
  roomServerUrl?: string

  /** Base URL for the Game Manager service (playground only, used to auto-create games) */
  gameManagerUrl?: string

  /**
   * RevenueCat Web Billing public API key. Required for `iap.getSubscriptions`,
   * `iap.purchaseSubscription`, `iap.isUserSubscribed`, and `iap.hasUserMadePurchase`
   * in playground mode. Wired in by the Vite plugin from per-target defaults in
   * `firebaseConfigs.ts` or the `RUNDOT_REVENUECAT_WEB_BILLING_KEY` env var.
   */
  revenueCatPublicApiKey?: string

  /** Emulator hosts for local development */
  functionsEmulatorHost?: string
  firestoreEmulatorHost?: string
  authEmulatorHost?: string

  /** RPC configuration */
  pollingInterval?: number
  rpcMaxRetries?: number
  rpcRetryDelayMs?: number

  /** Whether the project has server config files (config.json / config/*.config.json) */
  hasServerConfig?: boolean
  /** Version tag to use for simulation API calls (e.g. "private") */
  versionTag?: string

  /**
   * Whether a headless key is configured for this dev server. The raw key is
   * NEVER serialized to the browser — when true, the SDK fetches a short-lived
   * custom token from the `/__rundot-headless-auth` dev-server middleware
   * (which performs the key exchange Node-side). True only for an `rk_` key on a
   * playground target (or an opted-in internal QA key).
   */
  hasHeadlessAuth?: boolean

  /** Trades `previewTicket` for a custom token; presence selects preview sign-in. */
  previewTokenUrl?: string

  /** Signed grant presented to `previewTokenUrl`. */
  previewTicket?: string

  /** Selects `inMemoryPersistence`; every storage API throws at an opaque origin. */
  sandboxedOrigin?: boolean

  /**
   * Allow anonymous Firebase sign-in on the primary playground tab instead of
   * requiring Google sign-in. Google OAuth cannot run from a non-`localhost`
   * origin (Firebase authorized-domains), so a creator loading the dev server
   * from a phone on the LAN would otherwise hang forever on "signing in".
   *
   * SECURITY: set ONLY by the dev server (`RundotGamePlaygroundPlugin`) when it
   * is a local `vite serve` bound to a non-loopback host AND no headless key is
   * configured. It is never set for a production build, so the hosted playground
   * always keeps Google sign-in mandatory. The browser must not infer this from
   * `window.location`.
   */
  allowLanAnonymousAuth?: boolean

  /** SDK version string (injected by Vite plugin from package.json) */
  sdkVersion?: string

  /**
   * Suggested display name for the auto-created playground game (package.json
   * `name` at the project root, else the project-root directory basename).
   * Injected by the Vite plugin; falls back to 'Playground Game' when absent.
   */
  suggestedGameName?: string

  /** Override the app role returned by app.getMyRole() in playground mode.
   *  Useful for testing admin flows without needing backend role assignment. */
  appRole?: 'owner' | 'editor' | 'none'
}

/**
 * Global interface for playground config injected by Vite
 */
declare global {
  interface Window {
    __RUNDOT_GAME_PLAYGROUND__?: PlaygroundConfig
    __RUNDOT_GAME_PLAYGROUND_SIGN_OUT__?: () => Promise<void>
    __RUNDOT_GAME_PLAYGROUND_SIGN_IN_AS__?: (playerId: string) => Promise<void>
    __RUNDOT_GAME_PLAYGROUND_SIGN_IN_WITH_GOOGLE__?: () => Promise<void>
    /** Switch to a different playground player profile (dev/staging only).
     * Re-authenticates with a synthetic UID derived from the real user + playerId.
     * Pass undefined to switch back to the real (default) profile. */
    __RUNDOT_GAME_PLAYGROUND_SWITCH_PLAYER__?: (playerId?: string) => Promise<void>
    /** Tab coordinator info for playground multi-tab support. */
    __RUNDOT_GAME_PLAYGROUND_TAB_INFO__?: { slot: number; tabId: string; playerId: string | null }
  }

  // eslint-disable-next-line no-var
  var __RUNDOT_PLAYGROUND_HOST_FACTORY__: ((rundotGameApi: any) => any) | undefined
}

/**
 * Read playground configuration from the global object.
 * Vite injects the config into `window.__RUNDOT_GAME_PLAYGROUND__` during dev builds.
 */
export function getPlaygroundConfig(): PlaygroundConfig | null {
  if (typeof globalThis === 'undefined') {
    return null
  }

  const config = (globalThis as { __RUNDOT_GAME_PLAYGROUND__?: PlaygroundConfig }).__RUNDOT_GAME_PLAYGROUND__
  if (!config?.enabled) {
    return null
  }

  return config
}

/**
 * Check if playground mode is enabled.
 */
export function isPlaygroundEnabled(): boolean {
  const config = getPlaygroundConfig()
  return config?.enabled === true
}

/**
 * The proxy path used to route requests through Vite dev server.
 * Must match RUNDOT_GAME_API_PROXY_PATH in RundotGamePlaygroundPlugin.ts.
 */
const RUNDOT_GAME_API_PROXY_PATH = '/__rundotgameapi'

/**
 * Build the base URL for Cloud Functions calls.
 *
 * For local (emulator): Direct URL to emulator (no CORS issues)
 * For dev/staging: Uses Vite proxy path to avoid CORS issues
 */
export function buildFunctionsBaseUrl(config: PlaygroundConfig): string {
  // If explicit backendUrl is provided, use it
  if (config.backendUrl) {
    return config.backendUrl.replace(/\/$/, '')
  }

  // In browser: always use Vite proxy to avoid CORS issues.
  // The proxy is configured by RundotGamePlaygroundPlugin to forward to the correct
  // backend (emulator for local, deployed Cloud Functions for dev/staging).
  // This avoids CORS issues in all browsers, including embedded ones like
  // Cursor's Simple Browser where emulator CORS handling is unreliable.
  if (typeof window !== 'undefined') {
    return RUNDOT_GAME_API_PROXY_PATH
  }

  // Fallback: construct direct URL (for non-browser contexts)
  if (config.firebaseConfig?.projectId && config.functionsRegion) {
    const region = config.functionsRegion || 'us-central1'
    if (config.target === 'local' && config.functionsEmulatorHost) {
      return `http://${config.functionsEmulatorHost}/${config.firebaseConfig.projectId}/${region}`
    }
    return `https://${region}-${config.firebaseConfig.projectId}.cloudfunctions.net`
  }

  throw new Error(
    `[RUN] Cannot determine backend URL for target "${config.target}". ` +
    `Either provide backendUrl or configure Firebase project settings.`
  )
}

/**
 * Parse a value that might be a number or string into a number.
 */
export function parsePlaygroundNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.length === 0) {
      return undefined
    }
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

/**
 * Get the Cloud Run base URL from playground config.
 * Used by HTTP-based APIs (UGC, ImageGen) for direct Cloud Run calls.
 *
 * @returns Cloud Run URL without trailing slash, defaults to localhost:3000
 */
export function getCloudRunUrl(): string {
  const config = getPlaygroundConfig()

  if (config && 'cloudRunUrl' in config && typeof config.cloudRunUrl === 'string') {
    return config.cloudRunUrl.replace(/\/$/, '')
  }

  // Default to localhost for local development
  return 'http://localhost:3000'
}

/**
 * Hosted multiplayer room-server base URL from playground config, if configured.
 * Only the `playground` target is wired with one (by the Vite plugin). When
 * absent, PlaygroundHost falls back to the local dev sidecar / offline mock.
 *
 * @returns room-server URL without trailing slash, or undefined when unset
 */
export function getPlaygroundRoomServerUrl(): string | undefined {
  const config = getPlaygroundConfig()
  if (config && typeof config.roomServerUrl === 'string' && config.roomServerUrl) {
    return config.roomServerUrl.replace(/\/$/, '')
  }
  return undefined
}
